import React, { useState, useEffect } from 'react';
import { ArrowUp } from 'lucide-react';

/**
 * Komponen BackToTop:
 * Tombol floating "Kembali ke Atas" yang muncul otomatis saat pengguna melakukan
 * scroll ke bawah, lengkap dengan indikator progress scroll emas, animasi halus,
 * dan efek visual modern bernuansa Khazanah (Emerald & Gold).
 */
const BackToTop: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;

            // Tampilkan tombol jika pengguna telah scroll lebih dari 280px
            setIsVisible(scrollTop > 280);

            // Hitung persentase scroll (0 - 100)
            if (docHeight > 0) {
                const progress = Math.min(100, Math.max(0, (scrollTop / docHeight) * 100));
                setScrollProgress(progress);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // Jalankan sekali saat mount

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Parameter SVG Progress Ring (keliling lingkaran)
    const radius = 22;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (scrollProgress / 100) * circumference;

    return (
        <div
            className={`fixed bottom-6 right-6 md:bottom-8 md:right-8 z-40 transition-all duration-300 ease-out transform ${
                isVisible
                    ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
                    : 'opacity-0 translate-y-8 scale-75 pointer-events-none'
            }`}
        >
            <div className="relative group">
                {/* Tooltip Hover */}
                <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-xl bg-gray-900/90 text-white text-[11px] font-bold font-outfit whitespace-nowrap shadow-lg backdrop-blur-xs opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none -translate-x-1 group-hover:translate-x-0">
                    Kembali ke Atas
                    <span className="ml-1.5 text-gold text-[10px] font-normal">
                        ({Math.round(scrollProgress)}%)
                    </span>
                    {/* Tooltip arrow */}
                    <div className="absolute top-1/2 -right-1 -translate-y-1/2 border-solid border-l-gray-900/90 border-l-4 border-y-transparent border-y-4 border-r-0" />
                </div>

                {/* Floating Button with Progress Ring */}
                <button
                    onClick={scrollToTop}
                    aria-label="Kembali ke atas halaman"
                    className="relative w-12 h-12 md:w-13 md:h-13 rounded-full flex items-center justify-center bg-gradient-to-tr from-[#083017] via-[#0F5B30] to-[#15733e] text-white shadow-xl shadow-emerald-950/30 border border-gold/40 hover:border-gold hover:shadow-2xl hover:shadow-emerald-900/50 hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer"
                >
                    {/* SVG Circular Progress Ring */}
                    <svg
                        className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none p-0.5"
                        viewBox="0 0 52 52"
                    >
                        {/* Background track circle */}
                        <circle
                            cx="26"
                            cy="26"
                            r={radius}
                            fill="none"
                            stroke="rgba(255, 255, 255, 0.15)"
                            strokeWidth="2.5"
                        />
                        {/* Active gold progress circle */}
                        <circle
                            cx="26"
                            cy="26"
                            r={radius}
                            fill="none"
                            stroke="#E5B94C"
                            strokeWidth="2.5"
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                            strokeLinecap="round"
                            className="transition-[stroke-dashoffset] duration-150 ease-linear"
                        />
                    </svg>

                    {/* Arrow Icon with Hover Float Animation */}
                    <ArrowUp
                        size={20}
                        className="text-white group-hover:-translate-y-1 transition-transform duration-200 relative z-10"
                        strokeWidth={2.5}
                    />
                </button>
            </div>
        </div>
    );
};

export default BackToTop;
